import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { inr, fmtDate, TX_META } from "../lib/format";

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState("");

  const load = () => api.get(`/transactions/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => {
    load();
    api.get("/lookups").then((r) => setLookups(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line

  const maps = useMemo(() => ({
    cat: Object.fromEntries((lookups?.categories || []).map((c) => [c.id, c.name])),
    tag: Object.fromEntries((lookups?.tags || []).map((t) => [t.id, t.name])),
    acc: Object.fromEntries((lookups?.accounts || []).map((a) => [a.id, a.name])),
    biz: Object.fromEntries((lookups?.businesses || []).map((b) => [b.id, b.name])),
    mem: Object.fromEntries((lookups?.members || []).map((m) => [m.id, m.display_name])),
    src: Object.fromEntries((lookups?.income_sources || []).map((s) => [s.id, s.name])),
  }), [lookups]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const tx = data.transaction;
  const meta = TX_META[tx.transaction_type];

  const postDraft = async () => {
    try {
      await api.post(`/transactions/${id}/post`);
      toast.success("Posted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    }
  };

  const voidTx = async () => {
    try {
      await api.post(`/transactions/${id}/void`, { reason });
      toast.success("Voided — balances reversed");
      setVoidOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not void");
    }
  };

  const deleteDraft = async () => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Draft deleted");
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const Row = ({ k, v }) => v ? <div className="flex justify-between py-2 border-b border-border last:border-0 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div> : null;

  return (
    <div className="px-5 pt-6 pb-10" data-testid="tx-detail-page">
      <button data-testid="tx-detail-back" onClick={() => navigate(-1)} className="h-11 w-11 -ml-2 flex items-center justify-center rounded-xl active:bg-secondary"><CaretLeft size={20} /></button>
      <div className="mt-2">
        <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${meta.bg} ${meta.color}`}>{meta.label}</span>
        {tx.status !== "posted" && <span className="ml-2 inline-block text-xs font-semibold px-3 py-1 rounded-full bg-secondary text-muted-foreground uppercase">{tx.status}</span>}
        <h1 className={`mt-3 text-4xl font-light tracking-tight tnum ${tx.status === "voided" ? "line-through text-muted-foreground" : ""}`}>{inr(tx.total_amount)}</h1>
        <p className="mt-1 text-muted-foreground">{tx.merchant || tx.description || meta.label} · {fmtDate(tx.transaction_date)}</p>
      </div>

      <div className="mt-6 bg-white border border-border rounded-2xl px-4 py-2">
        <Row k="From account" v={maps.acc[tx.source_account_id]} />
        <Row k="To account" v={maps.acc[tx.destination_account_id]} />
        <Row k="Earning member" v={maps.mem[tx.earning_member_id]} />
        <Row k="Income source" v={maps.src[tx.income_source_id]} />
        <Row k="Notes" v={tx.description} />
        <Row k="Void reason" v={tx.void_reason} />
      </div>

      {(tx.lines || []).length > 0 && (
        <div className="mt-4">
          <h2 className="text-sm font-heading font-semibold text-muted-foreground uppercase tracking-wider">Lines</h2>
          <div className="mt-2 space-y-2">
            {tx.lines.map((l) => (
              <div key={l.line_number} className="bg-white border border-border rounded-2xl p-4">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{maps.cat[l.category_id] || "Uncategorized"}</span>
                  <span className="font-semibold tnum">{inr(l.amount)}</span>
                </div>
                {l.tag_ids?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {l.tag_ids.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{maps.tag[t]}</span>)}
                  </div>
                )}
                {l.allocations?.length > 0 && l.allocations.map((a, i) => (
                  <p key={i} className="mt-1 text-xs text-muted-foreground">↳ {maps.biz[a.business_id]} · {inr(a.amount)}</p>
                ))}
                {l.notes && <p className="mt-1 text-xs text-muted-foreground">{l.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {tx.status === "draft" && (
          <>
            <button data-testid="tx-post-btn" onClick={postDraft} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform">Post transaction</button>
            <button data-testid="tx-delete-draft-btn" onClick={deleteDraft} className="w-full h-12 rounded-xl border border-destructive text-destructive font-medium">Delete draft</button>
          </>
        )}
        {tx.status === "posted" && (
          !voidOpen ? (
            <button data-testid="tx-void-btn" onClick={() => setVoidOpen(true)} className="w-full h-12 rounded-xl border border-destructive text-destructive font-medium">Void transaction</button>
          ) : (
            <div className="bg-white border border-border rounded-2xl p-4 space-y-2">
              <input data-testid="tx-void-reason" className="w-full h-11 px-3 rounded-xl bg-background border border-border text-sm" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <div className="flex gap-2">
                <button data-testid="tx-void-cancel" onClick={() => setVoidOpen(false)} className="flex-1 h-11 rounded-xl border border-border text-sm">Cancel</button>
                <button data-testid="tx-void-confirm" onClick={voidTx} className="flex-1 h-11 rounded-xl bg-destructive text-destructive-foreground text-sm font-medium">Confirm void</button>
              </div>
            </div>
          )
        )}
      </div>

      {data.audit?.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-heading font-semibold text-muted-foreground uppercase tracking-wider">History</h2>
          <div className="mt-2 bg-white border border-border rounded-2xl px-4 py-2">
            {data.audit.map((e) => (
              <p key={e.id} className="py-1.5 text-xs text-muted-foreground border-b border-border last:border-0">
                {e.event_type.replaceAll("_", " ")} · {fmtDate(e.created_at?.slice(0, 10))} · {maps.mem[e.actor_member_id] || ""}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
